import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { achievements } from '../../lib/api';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Award,
  CalendarDays,
  CheckCircle2,
  CircleDot,
  Dumbbell,
  Filter,
  Lock,
  Medal,
  Package,
  Salad,
  Search,
  Sparkles,
  Target,
  Trophy,
  Users,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18nInstance from '../../i18n';


const t = (key, fallback, options = {}) =>
  i18nInstance.t(key, typeof fallback === 'string' ? { defaultValue: fallback, ...options } : fallback);
const CATEGORY_META = {
  trainings: {
    labelKey: 'achievements.categories.training',
    Icon: Dumbbell,
    badge: 'bg-blue-100 text-blue-800 border-blue-200',
    accent: 'from-blue-400 to-cyan-500',
    dot: 'bg-blue-500',
  },
  schedule: {
    labelKey: 'achievements.categories.schedule',
    Icon: CalendarDays,
    badge: 'bg-purple-100 text-purple-800 border-purple-200',
    accent: 'from-purple-400 to-violet-600',
    dot: 'bg-purple-500',
  },
  tournaments: {
    labelKey: 'achievements.categories.tournament',
    Icon: Trophy,
    badge: 'bg-amber-100 text-amber-800 border-amber-200',
    accent: 'from-amber-400 to-orange-500',
    dot: 'bg-amber-500',
  },
  nutrition: {
    labelKey: 'achievements.categories.nutrition',
    Icon: Salad,
    badge: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    accent: 'from-emerald-400 to-teal-600',
    dot: 'bg-emerald-500',
  },
  equipment: {
    labelKey: 'achievements.categories.equipment',
    Icon: Package,
    badge: 'bg-lime-100 text-lime-800 border-lime-200',
    accent: 'from-lime-300 to-lime-600',
    dot: 'bg-lime-500',
  },
  courts: {
    labelKey: 'achievements.categories.courts',
    Icon: CircleDot,
    badge: 'bg-rose-100 text-rose-800 border-rose-200',
    accent: 'from-rose-400 to-pink-600',
    dot: 'bg-rose-500',
  },
  opponents: {
    labelKey: 'achievements.categories.opponents',
    Icon: Users,
    badge: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    accent: 'from-indigo-400 to-blue-600',
    dot: 'bg-indigo-500',
  },
  matches: {
    labelKey: 'achievements.categories.matches',
    Icon: Medal,
    badge: 'bg-red-100 text-red-800 border-red-200',
    accent: 'from-red-400 to-rose-600',
    dot: 'bg-red-500',
  },
  goals: {
    labelKey: 'achievements.categories.progress',
    Icon: Target,
    badge: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    accent: 'from-cyan-400 to-sky-600',
    dot: 'bg-cyan-500',
  },
  fitness: {
    labelKey: 'achievements.categories.fitness',
    Icon: Dumbbell,
    badge: 'bg-sky-100 text-sky-800 border-sky-200',
    accent: 'from-sky-400 to-blue-600',
    dot: 'bg-sky-500',
  },
  video: {
    labelKey: 'achievements.categories.special',
    Icon: Search,
    badge: 'bg-zinc-100 text-zinc-700 border-zinc-200',
    accent: 'from-zinc-400 to-zinc-600',
    dot: 'bg-zinc-500',
  },
  social: {
    labelKey: 'achievements.categories.social',
    Icon: Users,
    badge: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200',
    accent: 'from-fuchsia-400 to-purple-600',
    dot: 'bg-fuchsia-500',
  },
  clubs: {
    labelKey: 'achievements.categories.clubs',
    Icon: Trophy,
    badge: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    accent: 'from-yellow-300 to-amber-500',
    dot: 'bg-yellow-400',
  },
};

const STATUS_OPTIONS = [
  { value: '', labelKey: 'achievements.status.all' },
  { value: 'true', labelKey: 'achievements.status.unlocked' },
  { value: 'false', labelKey: 'achievements.status.locked' },
];

export const AchievementsPage = () => {
  const { t, i18n } = useTranslation();
  const [achievementsList, setAchievementsList] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ unlocked: null, category: '' });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const categories = useMemo(() => {
    const base = [
      { value: '', label: t('achievements.allCategories') },
      { value: 'trainings', label: t('achievements.categories.training') },
      { value: 'schedule', label: t('achievements.categories.schedule') },
      { value: 'tournaments', label: t('achievements.categories.tournament') },
      { value: 'matches', label: t('achievements.categories.matches') },
      { value: 'goals', label: t('achievements.categories.progress') },
      { value: 'fitness', label: t('achievements.categories.fitness') },
      { value: 'nutrition', label: t('achievements.categories.nutrition') },
      { value: 'equipment', label: t('achievements.categories.equipment') },
      { value: 'courts', label: t('achievements.categories.courts') },
      { value: 'opponents', label: t('achievements.categories.opponents') },
      { value: 'video', label: t('achievements.categories.special') },
      { value: 'social', label: t('achievements.categories.social') },
      { value: 'clubs', label: t('achievements.categories.clubs') },
    ];

    const returnedCategories = achievementsList
      .map((achievement) => achievement.category)
      .filter(Boolean)
      .filter((category, index, list) => list.indexOf(category) === index);

    return base.concat(
      returnedCategories
        .filter((category) => !base.some((item) => item.value === category))
        .map((category) => ({ value: category, label: category }))
    );
  }, [achievementsList, t]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [achievementsRes, statsRes] = await Promise.all([
        achievements.getAll(filters),
        achievements.getStats(),
      ]);
      setAchievementsList(Array.isArray(achievementsRes.data) ? achievementsRes.data : []);
      setStats(statsRes.data);
    } catch (error) {
      setLoadError(t('achievements.loadError'));
      toast.error(t('achievements.loadError'));
    } finally {
      setLoading(false);
    }
  }, [filters, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getCategoryMeta = (category) => CATEGORY_META[category] || {
    labelKey: 'achievements.categories.special',
    Icon: Award,
    badge: 'bg-zinc-100 text-zinc-700 border-zinc-200',
    accent: 'from-zinc-400 to-zinc-600',
    dot: 'bg-zinc-500',
  };

  const getCategoryLabel = (category) => {
    const cat = categories.find((item) => item.value === category);
    return cat ? cat.label : (category || t('achievements.categories.special'));
  };

  const localeDateString = (dateStr) => {
    const locale = i18n.language === 'ru' ? 'ru-RU' : i18n.language === 'fr' ? 'fr-FR' : 'en-GB';
    return new Date(dateStr).toLocaleDateString(locale);
  };

  const getProgressPercentage = (achievement) => {
    if (!achievement.progress_required) return achievement.unlocked ? 100 : 0;
    return Math.min((achievement.progress_current / achievement.progress_required) * 100, 100);
  };

  const unlockedCount = stats?.unlocked_count ?? achievementsList.filter((achievement) => achievement.unlocked).length;
  const totalCount = stats?.total_achievements ?? achievementsList.length;
  const completionPercentage = stats?.completion_percentage ?? (totalCount ? Math.round((unlockedCount / totalCount) * 100) : 0);
  const lockedCount = Math.max(totalCount - unlockedCount, 0);
  const inProgressCount = achievementsList.filter((achievement) => !achievement.unlocked && achievement.progress_current > 0).length;

  const recentUnlocks = useMemo(() => {
    return achievementsList
      .filter((achievement) => achievement.unlocked && achievement.date_unlocked)
      .sort((a, b) => new Date(b.date_unlocked) - new Date(a.date_unlocked))
      .slice(0, 3);
  }, [achievementsList]);

  const topCategoryStats = useMemo(() => {
    const categoryStats = stats?.category_stats || {};
    return Object.entries(categoryStats)
      .filter(([, value]) => value.total > 0)
      .sort(([, a], [, b]) => b.unlocked / b.total - a.unlocked / a.total)
      .slice(0, 5);
  }, [stats]);

  const summaryCards = [
    {
      label: t('achievements.unlocked'),
      value: `${unlockedCount}/${totalCount}`,
      detail: t('achievements.earnedAchievements'),
      Icon: Trophy,
      color: 'text-lime-700',
      testId: 'stats-total',
    },
    {
      label: t('achievements.completed'),
      value: `${completionPercentage}%`,
      detail: t('achievements.overallProgressDetail'),
      Icon: Target,
      color: 'text-emerald-700',
      testId: 'stats-completion',
    },
    {
      label: t('achievements.inProgress'),
      value: inProgressCount,
      detail: t('achievements.startedButLocked'),
      Icon: Sparkles,
      color: 'text-blue-700',
      testId: 'stats-progress',
    },
    {
      label: t('achievements.locked'),
      value: lockedCount,
      detail: t('achievements.stillToChase'),
      Icon: Lock,
      color: 'text-zinc-700',
      testId: 'stats-locked',
    },
  ];

  const renderSkeleton = () => (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      {[0, 1, 2, 3, 4, 5].map((item) => (
        <div key={item} className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="h-12 w-12 animate-pulse rounded-2xl bg-zinc-200" />
            <div className="h-7 w-24 animate-pulse rounded-full bg-zinc-100" />
          </div>
          <div className="mt-5 h-5 w-3/4 animate-pulse rounded bg-zinc-200" />
          <div className="mt-3 h-4 w-full animate-pulse rounded bg-zinc-100" />
          <div className="mt-2 h-4 w-2/3 animate-pulse rounded bg-zinc-100" />
          <div className="mt-6 h-2 w-full animate-pulse rounded bg-zinc-100" />
        </div>
      ))}
    </div>
  );

  const renderAchievementCard = (achievement) => {
    const category = getCategoryMeta(achievement.category);
    const CategoryIcon = category.Icon;
    const progressPercentage = getProgressPercentage(achievement);
    const hasStarted = achievement.progress_current > 0;
    const statusLabel = achievement.unlocked
      ? t('achievements.unlocked')
      : hasStarted
        ? t('achievements.inProgress')
        : t('achievements.locked');
    const statusClass = achievement.unlocked
      ? 'bg-lime-100 text-lime-800 border-lime-200'
      : hasStarted
        ? 'bg-blue-100 text-blue-800 border-blue-200'
        : 'bg-zinc-100 text-zinc-700 border-zinc-200';

    return (
      <article
        key={achievement.id}
        className={`group overflow-hidden rounded-2xl border bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-xl hover:shadow-zinc-200/70 ${
          achievement.unlocked ? 'border-lime-200' : 'border-zinc-200'
        }`}
        data-testid={`achievement-card-${achievement.id}`}
      >
        <div className={`h-1.5 bg-gradient-to-r ${achievement.unlocked ? 'from-lime-300 to-emerald-500' : category.accent}`} />
        <div className="p-5">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className={`flex h-14 w-14 items-center justify-center rounded-2xl border text-2xl ${
                achievement.unlocked
                  ? 'border-lime-200 bg-lime-50 shadow-inner shadow-lime-100'
                  : 'border-zinc-200 bg-zinc-100 grayscale'
              }`}>
                {achievement.icon || <Award size={24} />}
              </div>
              <div>
                <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold ${statusClass}`}>
                  {achievement.unlocked ? <CheckCircle2 size={13} /> : <Lock size={13} />}
                  {statusLabel}
                </span>
              </div>
            </div>
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-zinc-200 bg-zinc-50 text-zinc-600">
              <CategoryIcon size={18} />
            </div>
          </div>

          <h3 className="line-clamp-2 text-lg font-semibold tracking-tight text-zinc-950">{achievement.title}</h3>
          <p className="mt-2 line-clamp-3 min-h-[4.5rem] text-sm leading-6 text-zinc-500">{achievement.description}</p>

          <div className="mt-4 flex flex-wrap gap-2">
            <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${category.badge}`}>
              {getCategoryLabel(achievement.category)}
            </span>
            <span className="inline-flex rounded-full border border-zinc-200 bg-white px-3 py-1 text-xs font-semibold text-zinc-600">
              {t('achievements.requirement')} {achievement.progress_required || 0}
            </span>
          </div>

          <div className="mt-5 rounded-xl border border-zinc-100 bg-zinc-50 p-4">
            <div className="mb-2 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-zinc-500">
              <span>{t('achievements.categories.progress')}</span>
              <span>
                {achievement.progress_current || 0} / {achievement.progress_required || 0}
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-zinc-200">
              <div
                className={`h-full rounded-full transition-all ${achievement.unlocked ? 'bg-lime-500' : hasStarted ? 'bg-blue-500' : 'bg-zinc-400'}`}
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>

          {achievement.unlocked && achievement.date_unlocked && (
            <p className="mt-4 flex items-center gap-2 text-xs font-medium text-zinc-500">
              <CalendarDays size={14} />
              {t('achievements.unlockedOn', { date: localeDateString(achievement.date_unlocked) })}
            </p>
          )}
        </div>
      </article>
    );
  };

  return (
    <div className="min-h-screen bg-[#f6f7f4] px-4 pb-24 pt-4 lg:px-8 lg:pb-10 lg:pt-8" data-testid="achievements-page">
      <div className="mx-auto max-w-7xl space-y-6">
        <section className="relative overflow-hidden rounded-3xl bg-zinc-950 p-6 text-white shadow-2xl shadow-zinc-300/60 md:p-8">
          <div className="pointer-events-none absolute inset-0 opacity-25">
            <div className="absolute -right-16 top-5 h-60 w-60 rounded-full border border-lime-300" />
            <div className="absolute bottom-8 left-8 h-px w-72 rotate-6 bg-lime-300" />
            <div className="absolute bottom-20 left-1/2 h-px w-80 -rotate-12 bg-white/40" />
          </div>
          <div className="relative grid gap-8 lg:grid-cols-[minmax(0,1fr)_280px] lg:items-end">
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-lime-200">
                <span className="h-2 w-2 rounded-full bg-lime-300" />
                {t('achievements.heroKicker')}
              </div>
              <h1 className="max-w-3xl text-4xl font-semibold tracking-tight md:text-5xl">{t('achievements.title')}</h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-zinc-300 md:text-base">
                {t('achievements.heroBody')}
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/10 p-5 backdrop-blur">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-medium text-zinc-300">{t('achievements.overallProgress')}</span>
                <Trophy className="text-lime-300" size={21} />
              </div>
              <div className="text-4xl font-semibold tracking-tight text-white">{completionPercentage}%</div>
              <div className="mt-4 h-2 rounded-full bg-white/15">
                <div className="h-2 rounded-full bg-lime-300" style={{ width: `${Math.min(completionPercentage, 100)}%` }} />
              </div>
            </div>
          </div>
        </section>

        <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {summaryCards.map(({ label, value, detail, Icon, color, testId }) => (
            <div key={label} className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm" data-testid={testId}>
              <div className="mb-4 flex items-center justify-between">
                <span className="text-sm font-medium text-zinc-500">{label}</span>
                <Icon className={color} size={20} />
              </div>
              <div className={`text-3xl font-semibold tracking-tight ${color}`}>{value}</div>
              <p className="mt-1 text-sm text-zinc-500">{detail}</p>
            </div>
          ))}
        </section>

        <section className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
          <div className="space-y-5">
            <div className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm">
              <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-zinc-700">
                <Filter size={18} />
                {t('common.filter')}
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-zinc-500">
                    {t('achievements.category')}
                  </label>
                  <select
                    className="min-h-11 w-full rounded-full border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm font-semibold text-zinc-800 outline-none transition focus:border-lime-300 focus:bg-white focus:ring-4 focus:ring-lime-100"
                    value={filters.category}
                    onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                    data-testid="filter-category"
                  >
                    {categories.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-zinc-500">
                    {t('achievements.statusLabel')}
                  </label>
                  <select
                    className="min-h-11 w-full rounded-full border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm font-semibold text-zinc-800 outline-none transition focus:border-lime-300 focus:bg-white focus:ring-4 focus:ring-lime-100"
                    value={filters.unlocked === null ? '' : filters.unlocked.toString()}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        unlocked: e.target.value === '' ? null : e.target.value === 'true',
                      })
                    }
                    data-testid="filter-status"
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {t(option.labelKey)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {loading && renderSkeleton()}

            {!loading && loadError && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center">
                <AlertTriangle className="mx-auto text-rose-600" size={36} />
                <h2 className="mt-4 text-xl font-semibold tracking-tight text-rose-950">{t('achievements.loadErrorTitle')}</h2>
                <p className="mt-2 text-sm text-rose-700">{loadError}</p>
                <button
                  type="button"
                  onClick={fetchData}
                  className="mt-5 inline-flex min-h-11 items-center justify-center rounded-full bg-rose-600 px-5 text-sm font-semibold text-white"
                >
                  {t('common.retry')}
                </button>
              </div>
            )}

            {!loading && !loadError && achievementsList.length === 0 && (
              <div className="rounded-3xl border border-dashed border-zinc-300 bg-white p-10 text-center shadow-sm">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-zinc-950 text-lime-300">
                  <Award size={31} />
                </div>
                <h2 className="mt-5 text-2xl font-semibold tracking-tight text-zinc-950">{t('achievements.emptyViewTitle')}</h2>
                <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-zinc-500">
                  {t('achievements.emptyViewBody')}
                </p>
              </div>
            )}

            {!loading && !loadError && achievementsList.length > 0 && (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {achievementsList.map(renderAchievementCard)}
              </div>
            )}
          </div>

          <aside className="space-y-5">
            <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold tracking-tight text-zinc-950">{t('achievements.recentUnlocks')}</h2>
                  <p className="text-sm text-zinc-500">{t('achievements.recentUnlocksBody')}</p>
                </div>
                <Sparkles className="text-lime-600" size={21} />
              </div>

              {recentUnlocks.length > 0 ? (
                <div className="space-y-3">
                  {recentUnlocks.map((achievement) => {
                    const category = getCategoryMeta(achievement.category);
                    return (
                      <div key={achievement.id} className="rounded-xl border border-lime-100 bg-lime-50 p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white text-xl">
                            {achievement.icon || <Award size={20} />}
                          </div>
                          <div className="min-w-0">
                            <p className="truncate font-semibold text-zinc-950">{achievement.title}</p>
                            <p className="mt-1 text-xs font-medium text-lime-800">
                              {getCategoryLabel(achievement.category)} - {localeDateString(achievement.date_unlocked)}
                            </p>
                            <span className={`mt-2 inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${category.badge}`}>
                              {t('achievements.unlocked')}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-xl border border-zinc-100 bg-zinc-50 p-4 text-sm leading-6 text-zinc-600">
                  {t('achievements.noRecentUnlocks')}
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold tracking-tight text-zinc-950">{t('achievements.categoryProgress')}</h2>
                  <p className="text-sm text-zinc-500">{t('achievements.categoryProgressBody')}</p>
                </div>
                <Target className="text-zinc-700" size={21} />
              </div>

              {topCategoryStats.length > 0 ? (
                <div className="space-y-4">
                  {topCategoryStats.map(([category, value]) => {
                    const meta = getCategoryMeta(category);
                    const percent = value.total ? Math.round((value.unlocked / value.total) * 100) : 0;
                    return (
                      <div key={category}>
                        <div className="mb-2 flex items-center justify-between gap-3 text-sm">
                          <span className="flex min-w-0 items-center gap-2 font-medium text-zinc-700">
                            <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${meta.dot}`} />
                            <span className="truncate">{getCategoryLabel(category)}</span>
                          </span>
                          <span className="shrink-0 font-semibold text-zinc-950">
                            {value.unlocked}/{value.total}
                          </span>
                        </div>
                        <div className="h-2 overflow-hidden rounded-full bg-zinc-200">
                          <div className="h-2 rounded-full bg-lime-500" style={{ width: `${percent}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-xl border border-zinc-100 bg-zinc-50 p-4 text-sm leading-6 text-zinc-600">
                  {t('achievements.noCategoryStats')}
                </div>
              )}
            </div>
          </aside>
        </section>
      </div>
    </div>
  );
};
